import useSafeInfo from './useSafeInfo'
import { useState, useEffect, useCallback } from 'react'
import { convertTokenIdToEVMAddress, checkIsPureERC20Token } from '@/utils/hedera'
import useChainId from './useChainId'
import { mirrorNodeFetch } from '@/services/mirrorNodeCache'

const TOKEN_URL = '/api/v1/accounts/{address}/tokens'

export interface Token {
  automatic_association: boolean
  balance: number
  created_timestamp: string
  decimals: number
  token_id: string
  freeze_status: string
  kyc_status: string
}

export interface TokensResponse {
  tokens: Token[]
  links: {
    next: string | null
  }
}

const EMPTY_DATA: TokensResponse = {
  tokens: [],
  links: { next: null },
}

export const getApprovedTokens = async (address: string, chainId: string): Promise<TokensResponse> => {
  try {
    const path = TOKEN_URL.replace('{address}', address)
    const data = await mirrorNodeFetch<TokensResponse>(path, chainId)
    return data ?? EMPTY_DATA
  } catch (error) {
    console.error({ error })
    return EMPTY_DATA
  }
}

export const useGetApprovedTokens = () => {
  const { safeAddress } = useSafeInfo()
  const [tokens, setTokens] = useState<TokensResponse>(EMPTY_DATA)
  const [loading, setLoading] = useState<boolean>(true)
  const chainId = useChainId()

  useEffect(() => {
    if (!safeAddress) return

    const timer = setTimeout(
      () =>
        getApprovedTokens(safeAddress, chainId).then((response) => {
          setTokens(response)
          setLoading(false)
        }),
      100,
    )
    return () => {
      clearTimeout(timer)
    }
  }, [safeAddress, chainId])

  return [tokens, loading] as const
}

export const useValidateTokenApproved = () => {
  const chainId = useChainId()

  const checkTokenApproved = useCallback(
    async (safeAddress: string, tokenAddress: string) => {
      if (!safeAddress || !tokenAddress) return false

      const isPureErc20 = await checkIsPureERC20Token(tokenAddress, chainId)
      if (isPureErc20) return true

      try {
        const data = await getApprovedTokens(safeAddress, chainId)

        if (!data.tokens) return false

        return data.tokens.some((token: Token) => {
          const evmAddress = convertTokenIdToEVMAddress(token.token_id)
          return evmAddress.toLowerCase() === tokenAddress.toLowerCase()
        })
      } catch (error) {
        console.error('[useIsTokenApproved]: Error checking Token Association:', error)
        return false
      }
    },
    [chainId],
  )

  return checkTokenApproved
}

const useIsTokenApproved = (tokenAddress: string) => {
  const [isApproved, setIsApproved] = useState<boolean>(false)
  const checkTokenApproved = useValidateTokenApproved()
  const { safeAddress } = useSafeInfo()

  useEffect(() => {
    checkTokenApproved(safeAddress, tokenAddress).then(setIsApproved)
  }, [tokenAddress, checkTokenApproved, safeAddress])

  return isApproved
}

export default useIsTokenApproved
